// Registers custom code components so Plasmic Studio can discover them
// through the app host (see PlasmicHost.jsx). This file has no default
// export and renders nothing — importing it for its side effects is enough.
//
// As you add more hand-built code components, register them here too.
import { registerComponent } from "@plasmicapp/host";
import AutoResizeTextarea from "./AutoResizeTextarea";
import OP1WebWindowManagerSurface from "./OP1WebWindowManagerSurface";
import OP1WebAppWindow from "./OP1WebAppWindow";
import SpaceDockButton from "./SpaceDockButton";
import OP1WebButtonAppWindow from "./OP1WebButtonAppWindow";
import OP1WebFrameWindow from "./OP1WebFrameWindow";
import OP1WebFrameWindowControl from "./OP1WebFrameWindowControl";
import windowRegistry, { spaceRegistry } from "./windowRegistry";

registerComponent(AutoResizeTextarea, {
  name: "AutoResizeTextarea",
  displayName: "Auto-Resize Textarea",
  description:
    "A writing-surface textarea that grows with its content instead of behaving like a fixed-size form field.",
  section: "Forum editor",
  importPath: "./AutoResizeTextarea",
  isDefaultExport: true,
  // Plasmic passes generated styling through this prop; every visual
  // property (background, border, radius, shadow, typography, spacing,
  // width, alignment) is controlled from the Studio style panel via
  // the class this prop receives.
  classNameProp: "className",
  styleSections: true,
  defaultStyles: {
    width: "100%",
    background: "transparent",
    borderWidth: "0px",
    borderStyle: "none",
    borderColor: "transparent",
    boxShadow: "none",
    borderRadius: "0px",
    padding: "4px 0px",
    fontSize: "16px",
    lineHeight: "1.4",
  },
  // Makes `value` behave as a real two-way state in Studio: content typed
  // in the canvas persists, and `value`/`onChange` can be wired into
  // whatever save-draft/publish logic gets added later, or read from
  // other elements via the data picker.
  states: {
    value: {
      type: "writable",
      variableType: "text",
      valueProp: "value",
      onChangeProp: "onChange",
    },
  },
  // Lets Studio interactions (and other components) call these directly,
  // e.g. "On click of X -> AutoResizeTextarea1.focus()".
  refActions: {
    focus: {
      displayName: "Focus",
      description: "Move keyboard focus into the field.",
      argTypes: [],
    },
    blur: {
      displayName: "Blur",
      description: "Remove keyboard focus from the field.",
      argTypes: [],
    },
    select: {
      displayName: "Select all text",
      argTypes: [],
    },
  },
  props: {
    value: {
      type: "string",
      displayName: "Value",
      description: "Initial text shown in Studio's canvas / used as the starting value.",
      // In Studio this edits the live canvas state; in generated code it's
      // written out as `defaultValue` instead, since there's no app state
      // wired up yet for a freshly-dropped instance.
      editOnly: true,
      uncontrolledProp: "defaultValue",
    },
    placeholder: {
      type: "string",
      displayName: "Placeholder",
      defaultValue: "Type here...",
    },
    minRows: {
      type: "number",
      displayName: "Min rows",
      description: "How many lines tall the field starts / never shrinks below.",
      defaultValue: 1,
    },
    maxRows: {
      type: "number",
      displayName: "Max rows",
      description: "Optional. Leave empty to grow indefinitely (page scrolls, not the field).",
      advanced: true,
    },
    autoFocus: {
      type: "boolean",
      displayName: "Auto focus",
      defaultValue: false,
      advanced: true,
    },
    disabled: {
      type: "boolean",
      displayName: "Disabled",
      defaultValue: false,
    },
    readOnly: {
      type: "boolean",
      displayName: "Read only",
      defaultValue: false,
      advanced: true,
    },
    name: {
      type: "string",
      displayName: "Name",
      advanced: true,
    },
    id: {
      type: "string",
      displayName: "HTML id",
      advanced: true,
    },
    maxLength: {
      type: "number",
      displayName: "Max length",
      advanced: true,
    },
    submitOnEnter: {
      type: "boolean",
      displayName: "Enter key does something special",
      description: "When on, Enter (without Shift) is prevented from inserting a newline and fires \"On Enter press\" instead. Useful for a title field.",
      defaultValue: false,
    },
    dataTestId: {
      type: "string",
      displayName: "Test id",
      description: "Sets a data-testid attribute for automated testing.",
      advanced: true,
    },
    placeholderColor: {
      type: "color",
      displayName: "Placeholder color",
      description: "Browsers don't let Studio's style panel reach the placeholder text directly, so it's exposed here instead.",
      advanced: true,
    },
    placeholderOpacity: {
      type: "number",
      displayName: "Placeholder opacity (0-1)",
      advanced: true,
    },
    onChange: {
      type: "eventHandler",
      displayName: "On change",
      argTypes: [{ name: "value", type: "string" }],
    },
    onEnterPress: {
      type: "eventHandler",
      displayName: "On Enter press",
      argTypes: [{ name: "value", type: "string" }],
      hidden: (props) => !props.submitOnEnter,
    },
    onFocus: {
      type: "eventHandler",
      displayName: "On focus",
      argTypes: [],
      advanced: true,
    },
    onBlur: {
      type: "eventHandler",
      displayName: "On blur",
      argTypes: [],
      advanced: true,
    },
    onKeyDown: {
      type: "eventHandler",
      displayName: "On key down",
      argTypes: [],
      advanced: true,
    },
    onInput: {
      type: "eventHandler",
      displayName: "On input",
      argTypes: [],
      advanced: true,
    },
  },
  // Two ready-to-drop presets so title/body never need custom code or
  // manual prop tweaking after insertion.
  templates: {
    "Title field": {
      props: {
        placeholder: "title goes here...",
        minRows: 1,
        maxRows: 4,
        submitOnEnter: true,
      },
      styles: {
        fontSize: "28px",
        fontWeight: 600,
        lineHeight: "1.25",
      },
    },
    "Body field": {
      props: {
        placeholder: "Here goes the content for what you want to talk about...",
        minRows: 3,
      },
      styles: {
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "1.5",
      },
    },
  },
});

// --- Desktop system (engineering handoff: Website Shell / Desktop) -------
// Four code components exposed to Plasmic. All of them read/write the
// shared Desktop State Manager (see DesktopStateProvider.jsx), which is
// mounted around the app host's canvas (PlasmicHost.jsx) and around the
// real app (App.js), so behavior here matches what ships.

registerComponent(OP1WebWindowManagerSurface, {
  name: "OP1WebWindowManagerSurface",
  displayName: "OP1WebWindowManagerSurface",
  description:
    "The floating-window area for the Desktop. Holds ONLY OP1WebAppWindow instances (the dock is a sibling, not a child). A window's header always stays inside this area. Can look empty - its job is behavior (the coordinate surface), not visuals.",
  section: "Desktop system",
  importPath: "./OP1WebWindowManagerSurface",
  isDefaultExport: true,
  classNameProp: "className",
  styleSections: true,
  defaultStyles: {
    width: "100%",
    height: "100%",
    minHeight: "400px",
  },
  props: {
    children: {
      type: "slot",
      displayName: "App windows",
    },
  },
});

registerComponent(OP1WebAppWindow, {
  name: "OP1WebAppWindow",
  displayName: "OP1WebAppWindow",
  description:
    "Behavioral shell around one app's Plasmic-designed content: drag, resize, focus, minimize/restore, close. Its header always stays inside the OP1WebWindowManagerSurface. The Window id must match an entry in windowRegistry.js.",
  section: "Desktop system",
  importPath: "./OP1WebAppWindow",
  isDefaultExport: true,
  classNameProp: "className",
  styleSections: true,
  props: {
    windowId: {
      type: "choice",
      displayName: "Window id",
      description:
        'Which app window this shell controls. Must match an id in src/components/windowRegistry.js (e.g. "conversations", "notes").',
      options: Object.keys(windowRegistry),
    },
    title: {
      type: "string",
      displayName: "Title override",
      description: "Optional. Falls back to the title in windowRegistry.js.",
      advanced: true,
    },
    icon: {
      type: "imageUrl",
      displayName: "Icon",
      description:
        "Pictogram shown in the title bar, to the left of the title. Optional.",
    },
    iconAlt: {
      type: "string",
      displayName: "Icon alt text",
      description: "Accessible description of the icon image.",
      advanced: true,
    },
    children: {
      type: "slot",
      displayName: "App content",
    },
  },
  templates: {
    Conversations: { props: { windowId: "conversations" } },
    Notes: { props: { windowId: "notes" } },
  },
});

registerComponent(SpaceDockButton, {
  name: "SpaceDockButton",
  displayName: "Space Dock Button",
  description:
    "Connects a Dock item to Background Space selection (Members / Forum). Icon, label, and selected styling stay fully Plasmic-designed via the slot.",
  section: "Desktop system",
  importPath: "./SpaceDockButton",
  isDefaultExport: true,
  classNameProp: "className",
  styleSections: true,
  props: {
    spaceId: {
      type: "choice",
      displayName: "Space id",
      description: "Which background space this button selects.",
      options: Object.keys(spaceRegistry),
    },
    children: {
      type: "slot",
      displayName: "Icon / label",
    },
    onSelectedChange: {
      type: "eventHandler",
      displayName: "On selected change",
      argTypes: [{ name: "isSelected", type: "boolean" }],
      advanced: true,
    },
  },
  // Derived, read-only: lets Studio bind variants to whether this space
  // is the currently active Background Space.
  states: {
    isSelected: {
      type: "readonly",
      variableType: "boolean",
      onChangeProp: "onSelectedChange",
    },
  },
});

registerComponent(OP1WebButtonAppWindow, {
  name: "OP1WebButtonAppWindow",
  displayName: "OP1WebButtonAppWindow",
  description:
    "Dock button for one app window. Never touches the window directly - only sends open/focus/minimize actions. Looks different per state (closed / open / active); the design lives in one CSS module, so every instance updates together.",
  section: "Desktop system",
  importPath: "./OP1WebButtonAppWindow",
  isDefaultExport: true,
  classNameProp: "className",
  styleSections: true,
  props: {
    windowId: {
      type: "choice",
      displayName: "Window id",
      description:
        'Which app window this button controls. Must match an id in src/components/windowRegistry.js (e.g. "conversations", "notes").',
      options: Object.keys(windowRegistry),
    },
    icon: {
      type: "imageUrl",
      displayName: "Icon",
      description:
        "Pictogram shown on the button. Pick an image from your assets or upload a new one.",
    },
    iconAlt: {
      type: "string",
      displayName: "Icon alt text",
      description: "Accessible description of the icon image.",
      advanced: true,
    },
    previewState: {
      type: "choice",
      displayName: "Preview state",
      description:
        "Design-time only: force the button to look closed / open / active so you can see each variant in the canvas. Leave empty on real pages - the button then follows the real window.",
      options: ["closed", "open", "active"],
      advanced: true,
    },
    onOpenChange: {
      type: "eventHandler",
      displayName: "On open change",
      argTypes: [{ name: "isOpen", type: "boolean" }],
      advanced: true,
    },
    onFocusedChange: {
      type: "eventHandler",
      displayName: "On focused change",
      argTypes: [{ name: "isFocused", type: "boolean" }],
      advanced: true,
    },
    onStateChange: {
      type: "eventHandler",
      displayName: "On state change",
      argTypes: [{ name: "state", type: "string" }],
      advanced: true,
    },
  },
  // Derived, read-only: lets Studio bind variants to the button's state
  // ("closed" | "open" | "active") and to whether the window is open/focused.
  states: {
    state: {
      type: "readonly",
      variableType: "text",
      onChangeProp: "onStateChange",
    },
    isOpen: {
      type: "readonly",
      variableType: "boolean",
      onChangeProp: "onOpenChange",
    },
    isFocused: {
      type: "readonly",
      variableType: "boolean",
      onChangeProp: "onFocusedChange",
    },
  },
});

// --- PILOT: "frame" approach --------------------------------------------
// Code owns behaviour only; the look is designed in Studio through slots and
// style targets. Sits alongside OP1WebAppWindow (not a replacement yet).

registerComponent(OP1WebFrameWindow, {
  name: "OP1WebFrameWindow",
  displayName: "OP1WebFrameWindow (pilot)",
  description:
    "Behaviour-only window frame: drag, resize, focus, minimize/close, header kept inside the surface. Design it in Studio: put anything in the Header and Body slots, and restyle the header/body from the style panel.",
  section: "Desktop system",
  importPath: "./OP1WebFrameWindow",
  isDefaultExport: true,
  classNameProp: "className",
  styleSections: true,
  props: {
    windowId: {
      type: "choice",
      displayName: "Window id",
      description:
        'Which window this frame controls. Must match an id in src/components/windowRegistry.js (use "framePilot" for the pilot).',
      options: Object.keys(windowRegistry),
    },
    title: {
      type: "string",
      displayName: "Default title",
      description:
        "Only used when the Header slot is empty. Falls back to the title in windowRegistry.js.",
      advanced: true,
    },
    header: {
      type: "slot",
      displayName: "Header",
      description:
        "Design the title bar here. Anything in it drags the window, except buttons/links/inputs. Add Window Control pieces for close/minimize. Leave empty for a plain default header.",
    },
    children: {
      type: "slot",
      displayName: "Body",
    },
    headerClass: {
      type: "class",
      displayName: "Header style",
      description: "Style the header area (background, padding, border...).",
      selectors: [
        {
          selector: ':component[data-focused="true"] :self',
          label: "Window focused",
        },
      ],
    },
    bodyClass: {
      type: "class",
      displayName: "Body style",
      description: "Style the body area (background, padding...).",
      selectors: [
        {
          selector: ':component[data-focused="true"] :self',
          label: "Window focused",
        },
      ],
    },
  },
});

registerComponent(OP1WebFrameWindowControl, {
  name: "OP1WebFrameWindowControl",
  displayName: "OP1WebFrameWindowControl (pilot)",
  description:
    "A close or minimize action for the window it sits in. Put it in an OP1WebFrameWindow header; add any icon or text in its slot and style it freely.",
  section: "Desktop system",
  importPath: "./OP1WebFrameWindowControl",
  isDefaultExport: true,
  classNameProp: "className",
  styleSections: true,
  props: {
    action: {
      type: "choice",
      displayName: "Action",
      options: ["close", "minimize"],
      defaultValue: "close",
    },
    children: {
      type: "slot",
      displayName: "Icon / label",
    },
  },
});
